import { useState } from "react";
import { api, Contact } from "../lib/api";
import { useBusiness, switchBusiness } from "../lib/business";

export default function Import() {
  const [rows, setRows] = useState<Partial<Contact>[]>([]);
  const [filename, setFilename] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ new_count: number; updated_count: number; row_count: number } | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const { businesses } = useBusiness();
  const [companyId, setCompanyId] = useState<number | "">("");

  async function onFile(file: File) {
    setErr(null); setResult(null); setProgress(null);
    setFilename(file.name);
    const text = await file.text();
    try {
      const parsed = parseMasterEnriched(text);
      if (!parsed.length) {
        setErr("Couldn't find contact rows. Drop the master_enriched.html (or any HTML with a <tbody> of contact rows).");
      }
      setRows(parsed);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  async function confirm() {
    if (!companyId) { setErr("Choose the company these contacts belong to."); return; }
    setBusy(true); setErr(null); setResult(null);
    const CHUNK = 250;
    let newCount = 0, updatedCount = 0, rowCount = 0;
    setProgress({ done: 0, total: rows.length });
    try {
      for (let i = 0; i < rows.length; i += CHUNK) {
        const slice = rows.slice(i, i + CHUNK);
        const r = await api.import(slice, filename || undefined, Number(companyId));
        newCount     += r.new_count;
        updatedCount += r.updated_count;
        rowCount     += r.row_count;
        setProgress({ done: Math.min(i + CHUNK, rows.length), total: rows.length });
      }
      setResult({ new_count: newCount, updated_count: updatedCount, row_count: rowCount });
      setRows([]);
      setTimeout(() => switchBusiness(Number(companyId), "/outreach/contacts"), 1500);
    } catch (e) {
      setErr(`${e instanceof Error ? e.message : String(e)} (imported ${rowCount.toLocaleString()} of ${rows.length.toLocaleString()} before failure)`);
    } finally { setBusy(false); }
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tightish text-slate-900">Import contacts</h1>
        <p className="mt-1 text-sm text-slate-500">Drop the <code>master_enriched.html</code> from <code>Emails_MWC/</code> — or any HTML with the same table shape. Upserts by name + company.</p>
      </div>

      <div className="card p-5">
        <label className="flex h-40 cursor-pointer items-center justify-center rounded-md border-2 border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100">
          <input type="file" accept=".html,.htm" className="hidden" onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])} />
          <div className="text-sm text-slate-600">
            {filename ? <><strong>{filename}</strong> · click to choose another</> : "Drop master_enriched.html here, or click to choose"}
          </div>
        </label>
        {err && <div className="mt-3 rounded bg-rose-50 px-3 py-2 text-sm text-rose-700 ring-1 ring-rose-100">{err}</div>}

        {rows.length > 0 && !result && (
          <div className="mt-4 space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <Stat label="Rows parsed" value={rows.length.toLocaleString()} />
              <Stat label="With email" value={rows.filter(r => r.email).length.toLocaleString()} />
              <Stat label="Companies" value={String(new Set(rows.map(r => (r.company || "").toLowerCase().trim()).filter(Boolean)).size)} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-600">Import into company</label>
              <select className="!text-sm !py-2" value={companyId} onChange={e => setCompanyId(e.target.value ? Number(e.target.value) : "")}>
                <option value="">Choose…</option>
                {businesses.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <button className="btn-primary" onClick={confirm} disabled={busy || !companyId}>
              {busy
                ? `Importing ${progress?.done.toLocaleString()} / ${progress?.total.toLocaleString()}…`
                : `Confirm import → ${rows.length.toLocaleString()} rows`}
            </button>
            {busy && progress && (
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                <div className="h-full bg-slate-700 transition-[width]" style={{ width: `${(progress.done / progress.total) * 100}%` }} />
              </div>
            )}
            <p className="text-xs text-slate-500">Uploaded in batches of 250 so Cloudflare doesn't time out. Upserts by (name, company); existing curated fields are never overwritten.</p>
          </div>
        )}

        {result && (
          <div className="mt-4 rounded-md bg-emerald-50 p-4 text-sm text-emerald-800 ring-1 ring-emerald-100">
            Imported {result.row_count.toLocaleString()} rows · <strong>{result.new_count.toLocaleString()}</strong> new · <strong>{result.updated_count.toLocaleString()}</strong> updated.
            Redirecting to contacts…
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-slate-50 p-3">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="mt-1 text-lg font-semibold text-slate-900">{value}</div>
    </div>
  );
}

// ---------- master_enriched.html parser ----------

function parseMasterEnriched(html: string): Partial<Contact>[] {
  const tbody = html.match(/<tbody[^>]*>([\s\S]*?)<\/tbody>/i);
  const scope = tbody ? tbody[1] : html;
  const trs = scope.match(/<tr[\s\S]*?<\/tr>/gi) || [];

  // Header detection — map header text → column index
  const head = html.match(/<thead[\s\S]*?<\/thead>/i);
  const hCells = head ? (head[0].match(/<th[^>]*>([\s\S]*?)<\/th>/gi) || []).map(c => stripTags(c).toLowerCase()) : [];
  const col: Record<string, number> = {};
  hCells.forEach((h, i) => {
    if (/^name\b/.test(h)) col.name = i;
    else if (/(title|position|role)/.test(h)) col.title = i;
    else if (/(company|organisation|organization)/.test(h)) col.company = i;
    else if (/country/.test(h)) col.country = i;
    else if (/^type\b/.test(h)) col.type = i;
    else if (/^source\b/.test(h)) col.source = i;
    else if (/^email/.test(h)) col.email = i;
    else if (/^phone/.test(h)) col.phone = i;
    else if (/confidence/.test(h)) col.confidence = i;
    else if (/notes/.test(h)) col.notes = i;
  });

  const out: Partial<Contact>[] = [];
  for (const tr of trs) {
    const tds = tr.match(/<td[^>]*>([\s\S]*?)<\/td>/gi) || [];
    if (tds.length < 4) continue;
    const get = (k: string) => col[k] != null ? stripTags(tds[col[k]] || "") : "";
    const name = get("name");
    if (!name) continue;
    const emailTd = col.email != null ? (tds[col.email] || "") : "";
    // Pull alt emails out of <details><div>alt1<br>alt2</div></details>
    const altsMatch = emailTd.match(/<details[\s\S]*?<\/details>/i);
    let altEmails: string[] = [];
    if (altsMatch) {
      const inner = altsMatch[0].match(/<div[^>]*>([\s\S]*?)<\/div>/i);
      if (inner) altEmails = inner[1].split(/<br\s*\/?>/i).map(s => stripTags(s).trim()).filter(Boolean);
    }
    const emailVisible = stripTags(emailTd.replace(/<details[\s\S]*?<\/details>/i, "")).trim();
    const hrefMatch = tr.match(/href\s*=\s*"([^"]*)"/i);

    out.push({
      name,
      title:   get("title"),
      company: get("company"),
      country: get("country"),
      type:    get("type"),
      source:  get("source"),
      profile_url: hrefMatch ? hrefMatch[1] : null,
      email:   emailVisible || null,
      alt_emails: altEmails.length ? JSON.stringify(altEmails) : null,
      phone:   get("phone") || null,
      email_source: get("confidence") || null,
    });
  }
  return out;
}

function stripTags(s: string): string {
  return s.replace(/<[^>]+>/g, "").replace(/&nbsp;|&#160;/g, " ")
    .replace(/&amp;/g, "&").replace(/&quot;/g, '"').replace(/&#39;|&apos;/g, "'")
    .replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/\s+/g, " ").trim();
}
